/**
 * PublishVersionModal Component
 *
 * Modal dialog for publishing a new version of the manual.
 * Story 7-4
 */

import { useState } from 'react';

interface PublishVersionModalProps {
  onPublish: (versionNumber: string, changelog: string) => Promise<void>;
  onCancel: () => void;
  isPublishing?: boolean;
}

export function PublishVersionModal({ onPublish, onCancel, isPublishing }: PublishVersionModalProps) {
  const [versionNumber, setVersionNumber] = useState('');
  const [changelog, setChangelog] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!versionNumber.trim()) {
      newErrors.versionNumber = 'Versienummer is verplicht';
    } else if (!/^\d+\.\d+(\.\d+)?$/.test(versionNumber.trim())) {
      newErrors.versionNumber = 'Gebruik format: 1.0 of 1.0.0';
    }

    if (!changelog.trim()) {
      newErrors.changelog = 'Changelog is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await onPublish(versionNumber.trim(), changelog);
    } catch (error) {
      console.error('Publish error:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nieuwe Versie Publiceren</h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Version Number */}
            <div>
              <label htmlFor="versionNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Versienummer *
              </label>
              <input
                type="text"
                id="versionNumber"
                value={versionNumber}
                onChange={(e) => setVersionNumber(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.versionNumber ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="1.0, 1.1, 2.0"
              />
              {errors.versionNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.versionNumber}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Gebruik semantisch versienummer (bijv. 1.0, 1.1, 2.0)
              </p>
            </div>

            {/* Changelog */}
            <div>
              <label htmlFor="changelog" className="block text-sm font-medium text-gray-700 mb-1">
                Changelog *
              </label>
              <textarea
                id="changelog"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.changelog ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Wat is er nieuw in deze versie?

- Nieuwe sectie toegevoegd: X
- Sectie Y bijgewerkt
- Verbeteringen aan sectie Z"
              />
              {errors.changelog && (
                <p className="mt-1 text-sm text-red-600">{errors.changelog}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Beschrijf de wijzigingen in deze versie
              </p>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex">
                <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-yellow-700">
                  Dit maakt een nieuwe versie aan die zichtbaar is voor alle gebruikers.
                </p>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={isPublishing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isPublishing ? 'Publiceren...' : 'Publiceren'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
